<template>
  <div>
    <div v-for="sensor in sensorsList" :key="sensor.id">
      <div class="sensor-header">
        <div>
          <h3 class="base-heading formlayout-title vs-title">
            {{ sensor.name }}
          </h3>
          <vs-text v-if="additionalSensorDetails[sensor.id].infoMessage">{{
            additionalSensorDetails[sensor.id].infoMessage
          }}</vs-text>
        </div>
        <div class="sensor-config-buttons">
          <vs-button
            v-if="additionalSensorDetails[sensor.id].hasTriggers"
            icon="mdi-flag"
            type="toggle"
            title="Toggle Job Markers"
            :active="isShowJobFlagsActive(sensor.id)"
            @click="toggleShowJobFlags(sensor.id)"
          />
          <vs-button
            v-if="additionalSensorDetails[sensor.id].hasGroup"
            icon="mdi-chart-timeline-variant-shimmer"
            type="toggle"
            title="Toggle Aggregate"
            :active="isShowAggregateActive(sensor.id)"
            @click="toggleShowAggregate(sensor.id)"
          />
          <vs-button
            v-if="canConfigureSensors"
            icon="mdi-cog"
            type="toggle"
            title="Configure"
            @click="configureSensor(sensor)"
          />
        </div>
      </div>
      <div v-if="sensor.triggers">
        <div v-for="trigger in sensor.triggers" :key="trigger.id">
          <div v-if="sensor.triggers.length > 1" class="trigger-name">
            <vs-text type="caption" bold>Trigger: {{ trigger.name }}</vs-text>
          </div>
          <ApexSensorChartDetails
            :sensor="sensor"
            :trigger="trigger"
            :show-aggregate="isShowAggregateActive(sensor.id)"
            :show-jobs-flags="isShowJobFlagsActive(sensor.id)"
          />
          <TriggerDetailsPanel :trigger="trigger" />
        </div>
      </div>
      <div v-if="sensor.triggers.length === 0">
        <ApexSensorChartDetails
          :sensor="sensor"
          :show-aggregate="isShowAggregateActive(sensor.id)"
          :show-jobs-flags="isShowJobFlagsActive(sensor.id)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ApexSensorChartDetails from './ApexSensorChartDetails.vue'
import TriggerDetailsPanel from './TriggerDetailsPanel.vue'
import { SensorSummary } from '@/models/sensors-dto'
import moment from 'moment'
import _ from 'lodash'
import { computed, ref, toRef } from 'vue'
import { getAdminPortalConfig } from '@/api/config'
import { useQuery } from '@tanstack/vue-query'
import { useGetters } from 'vuex-composition-helpers'

const props = defineProps<{
  sensorsList: SensorSummary[]
}>()

const sensors = toRef(props, 'sensorsList')

const sensorsWithTriggers = sensors.value.filter((s) => s.triggers.length > 0).map((s) => s.id)
const showJobFlags = ref<string[]>(sensorsWithTriggers)
const showAggregate = ref<string[]>([])

const additionalSensorDetails = computed(() => {
  return _.keyBy(
    sensors.value.map((s) => ({
      id: s.id,
      hasTriggers: s.triggers.length > 0,
      hasGroup: s.triggers.some((t) => t.sensorIds.length > 0),
      adminPortalLink: `${config.value?.data.url}/organization/${s.owner}/automation/sensors/${s.id}`,
      infoMessage:
        s.triggers.length === 0
          ? 'This device has no triggers'
          : s.triggers.length > 1
            ? 'This device has multiple triggers'
            : s.triggers[0].sensorIds.length > 1
              ? 'This device is part of a group'
              : null,
    })),
    (s) => s.id,
  )
})

const toggleShowJobFlags = (sensorId: string) => {
  if (isShowJobFlagsActive(sensorId)) {
    showJobFlags.value = showJobFlags.value.filter((s) => s !== sensorId)
  } else {
    showJobFlags.value = [...showJobFlags.value, sensorId]
  }
}

const isShowJobFlagsActive = (sensorId: string) => {
  return showJobFlags.value?.indexOf(sensorId) !== -1
}

const toggleShowAggregate = (sensorId: string) => {
  if (isShowAggregateActive(sensorId)) {
    showAggregate.value = showAggregate.value.filter((s) => s !== sensorId)
  } else {
    showAggregate.value = [...showAggregate.value, sensorId]
  }
}

const isShowAggregateActive = (sensorId: string) => {
  return showAggregate.value?.indexOf(sensorId) !== -1
}

const { data: config } = useQuery({
  queryKey: ['config'],
  queryFn: getAdminPortalConfig,
  cacheTime: Infinity,
  staleTime: moment.duration(1, 'days').asMilliseconds(),
})

const { hasAccountOrganizationAccess } = useGetters('permissions', ['hasAccountOrganizationAccess'])

const canConfigureSensors = computed(() => {
  return hasAccountOrganizationAccess.value
})

const configureSensor = (sensor: SensorSummary) => {
  const link = additionalSensorDetails.value[sensor.id].adminPortalLink
  window.open(link, '_blank')
}
</script>

<style scoped>
.sensor-header {
  display: flex;
  justify-content: space-between;
  margin-top: var(--space-base);
}

.trigger-name {
  margin-top: var(--space-large);
}
.trigger-name p {
  color: var(--color-black) !important;
}
</style>
